import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useResizeObserver } from '@/hooks/useResizeObserver'
import { useCallback, useRef, useState } from 'react'
import type React from 'react'

interface TruncatedCellProps {
	children: React.ReactNode
	maxWidth?: number | string // Allow custom max width
	className?: string
	align?: 'left' | 'center' | 'right'
	showTooltip?: boolean
	tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
	maxTooltipWidth?: string
	tooltipContent?: string
	/**
	 * Override the default collision boundary. Useful when the cell is inside an `overflow-hidden` container
	 * (e.g., a bottom bar or drawer) that forces the tooltip to incorrectly flip its position.
	 */
	collisionBoundary?: Element | null | undefined
}

export function TruncatedCell({
	children,
	maxWidth,
	className = '',
	align = 'left',
	showTooltip = true,
	tooltipSide = 'top',
	maxTooltipWidth = 'max-w-md',
	tooltipContent,
	collisionBoundary,
}: TruncatedCellProps) {
	const textRef = useRef<HTMLDivElement | null>(null)
	const [isOverflowing, setIsOverflowing] = useState(false)

	const [node, setNode] = useState<HTMLDivElement | null>(null)
	const measureTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

	const measure = useCallback((element: HTMLDivElement | null) => {
		clearTimeout(measureTimeoutRef.current)
		if (!element) {
			return
		}
		measureTimeoutRef.current = setTimeout(() => setIsOverflowing(element.scrollWidth > element.clientWidth), 0)
	}, [])

	// A ref callback rather than an effect: wrapping the cell in a tooltip replaces this div, and the
	// observers must follow it.
	const observeOverflow = useCallback(
		(element: HTMLDivElement | null) => {
			textRef.current = element
			setNode(element)
			measure(element)
			if (!element) {
				return
			}
			// Text can change without the box changing, which a resize observer never reports.
			const mutationObserver = new MutationObserver(() => measure(element))
			mutationObserver.observe(element, { childList: true, subtree: true, characterData: true })
			return () => {
				clearTimeout(measureTimeoutRef.current)
				mutationObserver.disconnect()
				if (textRef.current === element) {
					textRef.current = null
				}
			}
		},
		[measure],
	)

	useResizeObserver(node, () => measure(node))

	const alignmentClass = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right',
	}[align]

	// Always apply truncation styles to prevent mount jitter. The Tooltip handles showing full text.
	const truncateStyles = 'overflow-hidden text-ellipsis whitespace-nowrap'

	// The padding, cancelled by the negative margin, moves the clip edge out so a focus ring on an icon at the
	// cell's edge keeps its outer side. The content stays where it was.
	const content = (
		<div
			ref={observeOverflow}
			className={`block -m-1 p-1 w-[calc(100%+--spacing(2))] min-w-0 ${truncateStyles} ${alignmentClass} ${className}`}
			style={
				maxWidth !== undefined
					? {
							maxWidth: `calc(${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth} + var(--spacing) * 2)`,
						}
					: undefined
			}>
			{children}
		</div>
	)

	// Only show tooltip if text is overflowing and showTooltip is true
	if (!isOverflowing || !showTooltip) {
		return content
	}

	return (
		<Tooltip>
			{/* The full text is already in the DOM; a tab stop per cell would bury the page's real controls. */}
			<TooltipTrigger asChild focusable={false}>
				{content}
			</TooltipTrigger>
			<TooltipContent
				side={tooltipSide}
				align='start'
				className={`${maxTooltipWidth} wrap-break-word`}
				sideOffset={8}
				avoidCollisions={true}
				// Use the custom boundary if provided, otherwise fallback to local containers or document body.
				// Explicitly checking `!== undefined` to allow passing `null` to bypass local boundaries.
				collisionBoundary={
					collisionBoundary !== undefined
						? collisionBoundary || undefined
						: textRef.current?.closest('[class*="bg-card"]') ||
							textRef.current?.closest('[class*="overflow-hidden"]') ||
							textRef.current?.closest('.CommonTable') ||
							document.body
				}
				collisionPadding={10}>
				{tooltipContent ? <p>{tooltipContent}</p> : <div>{children}</div>}
			</TooltipContent>
		</Tooltip>
	)
}
